import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { API_URL } from '../../injection-tokens';
import {
  FpcAuthenticateRequest,
  FpcAuthenticateResponse,
  ProblemDetails,
  State,
  SystemApiError,
  SystemNote,
} from './system-api.types';

export type {
  FpcAuthenticateRequest,
  FpcAuthenticateResponse,
  ProblemDetails,
  State,
  SystemApiError,
  SystemNote,
} from './system-api.types';

interface CachedStates {
  data: State[];
  timestamp: number;
}

const CACHE_KEY = 'system_api_states';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

@Injectable({ providedIn: 'root' })
export class SystemApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  authenticateFpc(payload: FpcAuthenticateRequest): Observable<FpcAuthenticateResponse> {
    return this.httpClient.post<FpcAuthenticateResponse>(this.buildFpcAuthenticateEndpoint(), payload).pipe(
      catchError((error: HttpErrorResponse) => this.toSystemApiError(error)),
    );
  }

  getStates(): Observable<State[]> {
    // Check cache first
    const cached = this.getCachedStates();
    if (cached) {
      return of(cached);
    }

    // Fetch from API if cache miss or expired
    const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
    const url = `${trimmedApiUrl}/System/State`;
    return this.httpClient.get<State[]>(url).pipe(
      tap((states) => this.setCachedStates(states)),
      catchError(() => {
        // Fallback: try to return stale cache on error
        const staleCache = this.getCachedStatesIgnoringExpiration();
        return staleCache ? of(staleCache) : of([]);
      })
    );
  }

  updateNote(note: SystemNote): Observable<SystemNote> {
    return this.httpClient.put<SystemNote>(this.buildNoteEndpoint(), note).pipe(
      catchError((error: HttpErrorResponse) => this.toSystemApiError(error))
    );
  }

  private getCachedStates(): State[] | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const parsed: CachedStates = JSON.parse(cached);
      const now = Date.now();

      // Check if cache has expired
      if (now - parsed.timestamp > CACHE_DURATION_MS) {
        return null; // Cache expired
      }

      return parsed.data;
    } catch {
      // localStorage unavailable or parse error
      return null;
    }
  }

  private buildNoteEndpoint(): string {
    const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
    return `${trimmedApiUrl}/System/Note`;
  }

  private buildFpcAuthenticateEndpoint(): string {
    const trimmedApiUrl = this.apiUrl.replace(/\/$/, '');
    return `${trimmedApiUrl}/System/FPC/FPCAuthenticate`;
  }

  private getProblemDetails(error: unknown): ProblemDetails | undefined {
    if (error && typeof error === 'object' && 'title' in error) {
      return error as ProblemDetails;
    }

    return undefined;
  }

  private getErrorMessage(error: HttpErrorResponse, problemDetails?: ProblemDetails): string {
    return problemDetails?.detail ?? problemDetails?.title ?? error.message;
  }

  private toSystemApiError(error: HttpErrorResponse): Observable<never> {
    const problemDetails = this.getProblemDetails(error.error);
    const systemApiError: SystemApiError = {
      status: error.status,
      message: this.getErrorMessage(error, problemDetails),
      problemDetails,
      originalError: error,
    };

    return throwError(() => systemApiError);
  }

  private getCachedStatesIgnoringExpiration(): State[] | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      const parsed: CachedStates = JSON.parse(cached);
      return parsed.data;
    } catch {
      return null;
    }
  }

  private setCachedStates(states: State[]): void {
    try {
      const cached: CachedStates = {
        data: states,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
    } catch {
      // localStorage unavailable; silently fail
    }
  }
}
